"use client";

/**
 * Shell de Rubripedia (catálogo público estilo docs). Header con logo +
 * búsqueda global + acceso a la plataforma, y cuerpo de 2-3 columnas: sidebar
 * izquierdo (categorías/tags) + contenido. El «On this page» lo aporta cada
 * página de detalle dentro de su propio contenido.
 */

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Search, ArrowRight } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { RubripediaSidebar } from "./RubripediaSidebar";

export function RubripediaShell({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [q, setQ] = useState("");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const term = q.trim();
    router.push(term ? `/rubripedia?q=${encodeURIComponent(term)}` : "/rubripedia");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] items-center gap-3 px-4 py-2.5 sm:px-6">
          <Link href="/rubripedia" className="shrink-0" aria-label="Rubripedia — inicio">
            <span className="text-[16px] font-bold tracking-tight">Rubripedia</span>
          </Link>

          <form onSubmit={submit} className="relative mx-auto w-full max-w-xl">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, ID, tag o categoría…"
              className="h-9 w-full rounded-lg border border-border bg-card pl-9 pr-3 text-[13px] outline-none placeholder:text-muted-foreground focus:border-primary/50"
            />
          </form>

          <div className="flex shrink-0 items-center gap-2">
            <ThemeToggle />
            <Link
              href="/auth/login"
              className="hidden items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-[13px] font-semibold text-primary-foreground transition-transform hover:scale-[1.03] sm:inline-flex"
            >
              Entrar <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </header>

      {/* Cuerpo */}
      <div className="mx-auto flex max-w-[1400px]">
        <RubripediaSidebar />
        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
}
